import { Permission, PermissionSets } from '../types';
import { mkPath, mkUrl, mkUrlNav, obscureUrls } from './_';
import { onSubdomain } from './general';
export const adminUrls = {
    manage: mkUrl(mkPath('manage')),
    propertiesHub: mkUrl(mkPath('manage', 'properties'), []),
    // On a community the page lists what the community contributed, which takes stories-read there.
    myProperties: mkUrlNav(mkPath('manage', 'properties', 'my-properties'), onSubdomain ? [Permission.StoriesRead, Permission.SystemDistricts] : [], {
        nav: {
            label: 'My Properties',
            description: 'View and manage your stories',
            icon: 'bi-house-heart',
            group: 'properties',
        },
    }),
    myPropertiesManage: mkUrl(mkPath('manage', 'properties', 'my-properties', 'manage', '$id'), []),
    myproperty: mkUrl(mkPath('manage', 'properties', 'my-properties', '$id'), []),
    properties: mkUrlNav(mkPath('manage', 'properties', 'list'), [...PermissionSets.propertyListing.full], {
        nav: {
            label: 'Property List',
            description: 'Browse, search, and edit all properties',
            icon: 'bi-building',
            group: 'properties',
        },
    }),
    geometry: mkUrlNav(mkPath('manage', 'properties', 'map-editor'), [...PermissionSets.mapGeo.full], {
        nav: {
            label: 'Map Editor',
            description: 'Draw and edit property boundaries on the map',
            icon: 'bi-map',
            group: 'properties',
        },
    }),
    propertyTags: mkUrlNav(mkPath('manage', 'properties', 'property-tags'), [...PermissionSets.propertyTags.full], {
        nav: {
            label: 'Property Tags',
            description: 'Define property tags and icons',
            icon: 'bi-tags',
            group: 'properties',
        },
    }),
    propertyTypes: mkUrlNav(mkPath('manage', 'properties', 'property-types'), [Permission.SystemDistricts], {
        nav: {
            label: 'Property Types',
            description: 'Define property types and map icons',
            icon: 'bi-geo-alt',
            group: 'properties',
        },
    }),
    communityHub: mkUrl(mkPath('manage', 'community'), []),
    discussions: mkUrlNav(mkPath('manage', 'community', 'discussions'), [], {
        nav: {
            label: 'Discussions',
            description: 'View and participate in discussions',
            icon: 'bi-chat-dots',
            group: 'community',
        },
    }),
    discussionsNew: mkUrl(mkPath('manage', 'community', 'discussions', 'new'), []),
    discussionsDetail: mkUrl(mkPath('manage', 'community', 'discussions', '$id'), []),
    contentDiscussionKinds: mkUrlNav(mkPath('manage', 'community', 'discussion-forums'), [...PermissionSets.discussionKinds.full], {
        nav: {
            label: 'Discussion Forums',
            description: 'Configure discussion categories and forums',
            icon: 'bi-chat-square-text',
            group: 'community',
        },
    }),
    moderation: mkUrlNav(mkPath('manage', 'community', 'moderation'), [...PermissionSets.moderation.full], {
        nav: {
            label: 'Discussion Moderation',
            description: 'Review flagged discussions and take action',
            icon: 'bi-shield-check',
            group: 'community',
        },
    }),
    tours: mkUrlNav(mkPath('manage', 'community', 'tours'), [...PermissionSets.tours.full, Permission.SystemDistricts], {
        nav: {
            label: 'Tours',
            description: 'Create and manage guided tours',
            icon: 'bi-signpost-split',
            group: 'community',
        },
    }),
    toursWizardNew: mkUrl(mkPath('manage', 'community', 'tours', 'wizard'), [...PermissionSets.tours.full, Permission.SystemDistricts]),
    toursWizard: mkUrl(mkPath('manage', 'community', 'tours', 'wizard', '$id'), [...PermissionSets.tours.full, Permission.SystemDistricts]),
    // Inviting homeowners is editing the tour, so it takes the right to change tours.
    toursInvites: mkUrl(mkPath('manage', 'community', 'tours', '$id', 'invites'), [Permission.ToursUpdate, Permission.SystemDistricts]),
    stories: mkUrlNav(mkPath('manage', 'community', 'stories'), [...PermissionSets.stories.full, Permission.SystemDistricts], {
        nav: {
            label: 'Stories',
            description: "Your community's stories about properties",
            icon: 'bi-book',
            group: 'community',
        },
    }),
    contentAnnouncements: mkUrlNav(mkPath('manage', 'community', 'announcements'), [...PermissionSets.content.announcements], {
        nav: {
            label: 'Announcements',
            description: 'Create and manage district announcements',
            icon: 'bi-megaphone',
            group: 'community',
        },
    }),
    contentAnnouncementsAddEdit: mkUrl(mkPath('manage', 'community', 'announcements', 'add-edit'), [...PermissionSets.content.announcements]),
    contentAnnouncementsEdit: mkUrl(mkPath('manage', 'community', 'announcements', 'add-edit', '$id'), [...PermissionSets.content.announcements]),
    contentEvents: mkUrlNav(mkPath('manage', 'community', 'events'), [...PermissionSets.content.events], {
        nav: {
            label: 'Events',
            description: 'Schedule and manage community events',
            icon: 'bi-calendar-event',
            group: 'community',
        },
    }),
    contentEventsAddEdit: mkUrl(mkPath('manage', 'community', 'events', 'add-edit'), [...PermissionSets.content.events]),
    contentEventsEdit: mkUrl(mkPath('manage', 'community', 'events', 'add-edit', '$id'), [...PermissionSets.content.events]),
    // Members (the screen's path predates the name)
    people: mkUrlNav(mkPath('manage', 'community', 'people'), [...PermissionSets.districtUsers.full], {
        nav: {
            label: 'Members',
            description: 'Add and remove members, and choose what they can do',
            icon: 'bi-person-lines-fill',
            group: 'community',
        },
    }),
    peopleDetail: mkUrl(mkPath('manage', 'community', 'people', 'detail', '$id'), [...PermissionSets.districtUsers.full]),
    // The Members screen replaced a "Users" list and a separate "Invitations" screen. Both paths are
    // in people's bookmarks and in links we have already sent out, so they redirect rather than 404.
    users: mkUrl(mkPath('manage', 'community', 'members'), [...PermissionSets.districtUsers.full]),
    invitations: mkUrl(mkPath('manage', 'community', 'invitations'), [...PermissionSets.community.invite]),
    contentHub: mkUrl(mkPath('manage', 'content'), []),
    contentHomepage: mkUrlNav(mkPath('manage', 'content', 'homepage'), [...PermissionSets.content.homepage], {
        nav: {
            label: 'Homepage',
            description: 'Edit homepage layout, content, and awards',
            icon: 'bi-house-door',
            section: 'Pages',
            group: 'content',
        },
    }),
    contentAbout: mkUrlNav(mkPath('manage', 'content', 'about'), [...PermissionSets.content.about], {
        nav: {
            label: 'About',
            description: 'Update about page and board members',
            icon: 'bi-info-circle',
            section: 'Pages',
            group: 'content',
        },
    }),
    contentResources: mkUrlNav(mkPath('manage', 'content', 'resources'), [...PermissionSets.content.resources], {
        nav: {
            label: 'Resources',
            description: 'Manage FAQs and downloadable forms',
            icon: 'bi-file-earmark-text',
            section: 'Resources',
            group: 'content',
        },
    }),
    contentBranding: mkUrlNav(mkPath('manage', 'content', 'branding'), [...PermissionSets.customization.theme], {
        nav: {
            label: 'Branding',
            description: 'Set your name, logo, contact info, and social links',
            icon: 'bi-postcard',
            group: 'content',
            organizationGroup: 'community',
        },
    }),
    contentTheme: mkUrlNav(mkPath('manage', 'content', 'theme'), [...PermissionSets.customization.theme], {
        nav: {
            label: 'Theme',
            description: 'Customize colors and preview them across the site',
            icon: 'bi-palette',
            group: 'content',
            organizationGroup: 'community',
        },
    }),
    contentBanners: mkUrlNav(mkPath('manage', 'content', 'banners'), [...PermissionSets.customization.banners], {
        nav: {
            label: 'Banners',
            description: 'Create site-wide notification banners',
            icon: 'bi-flag',
            group: 'content',
        },
    }),
    contentAds: mkUrlNav(mkPath('manage', 'content', 'ad-manager'), [...PermissionSets.customization.ads], {
        nav: {
            label: 'Ad Manager',
            description: 'Configure advertisement placements',
            icon: 'bi-badge-ad',
            group: 'content',
        },
    }),
    directorTypes: mkUrlNav(mkPath('manage', 'content', 'directors'), [...PermissionSets.directorTypes.full], {
        nav: {
            label: 'Director Types',
            description: 'Configure director types',
            icon: 'bi-person-vcard',
            group: 'content',
        },
    }),
};
// Paths, permissions and nav only: what the user menu needs to tell who can manage a community
export const adminUrlsArray = obscureUrls(adminUrls);
